import React, { useState } from 'react';
import ZentorgaForm from '../components/ZentorgaForm';
import SurveyQuestion from '../components/SurveyQuestion';
import BenefitPeriod from '../../components/BenefitPeriod';
import KeepButton from '../components/KeepButton';
import SoundVideo from '../components/SoundVideo';
import { useFormSubmit } from '../hooks/useFormSubmit';
import { useMultiStep } from '../hooks/useMultiStep';
import { getImagesByStep } from '../utils/templateMapper';
import '../styles/zentorga.scss';
import '../styles/template25.scss';

/**
 * Template25 - 두 단계 템플릿 (2개 설문 포함)
 * Step 1: 1개 이미지(imgUrl1) + Keep 버튼
 * Step 2: 나머지 이미지(imgUrl2-6) + 설문 2개 + 혜택기간 + 폼 + 푸터
 */
const Template25 = ({
  landingData,
  urlCode,
  urlCodeSettingId
}) => {
  const { handleSubmit, isSubmitting } = useFormSubmit(
    urlCode,
    urlCodeSettingId,
    landingData.endpoint
  );

  const { currentStep, goToNextStep } = useMultiStep(2);
  const [survey1Answer, setSurvey1Answer] = useState('');
  const [survey2Answer, setSurvey2Answer] = useState('');

  const stepImages = getImagesByStep(landingData, 2);

  const handleKeepButton = () => {
    goToNextStep({
      selectedOption: survey1Answer,
      selectedOption2: survey2Answer
    });
  };

  const handleFormSubmit = async (formData) => {
    const submitData = {
      ...formData,
      selectedOption: survey1Answer,
      selectedOption2: survey2Answer
    };
    await handleSubmit(submitData);
  };

  return (
    <section className="template25-layout zentorga-layout">
      {/* Step 1 */}
      {currentStep === 0 && (
        <div className="step1 step-container">
          {stepImages.step1.length > 0 && (
            <div className="template25-images zentorga-images">
              {stepImages.step1.map((img, idx) => {
                const fileExtension = img.url.split('.').pop().toLowerCase();
                const isVideo = ['mp4', 'webm', 'ogg', 'mov'].includes(fileExtension);
                return isVideo ? (
                  <SoundVideo key={idx} src={img.url} loop={img.alt !== "false"} />
                ) : (
                  <img key={idx} src={img.url} alt={img.alt} />
                );
              })}
            </div>
          )}

          <KeepButton
            onClick={handleKeepButton}
            imageUrl={landingData.keepBtnUrl}
            text="다음 단계"
          />
        </div>
      )}

      {/* Step 2 */}
      {currentStep === 1 && (
        <div className="step2 step-container">
          {stepImages.step2.length > 0 && (
            <div className="template25-images zentorga-images">
              {stepImages.step2.map((img, idx) => {
                const fileExtension = img.url.split('.').pop().toLowerCase();
                const isVideo = ['mp4', 'webm', 'ogg', 'mov'].includes(fileExtension);
                return isVideo ? (
                  <SoundVideo key={idx} src={img.url} loop={img.alt !== "false"} />
                ) : (
                  <img key={idx} src={img.url} alt={img.alt} />
                );
              })}
            </div>
          )}

          <div className="surveySection">
            <SurveyQuestion
              topic={landingData.surveyTitle1 || '첫 번째 설문'}
              answers={[
                landingData.surveyOpt1_1,
                landingData.surveyOpt1_2,
                landingData.surveyOpt1_3,
                landingData.surveyOpt1_4,
                landingData.surveyOpt1_5
              ].filter(Boolean)}
              onChange={setSurvey1Answer}
              questionNumber={1}
              displayType={landingData.surveyTopic?.topic?.[0]?.type || 'radio'}
            numberColor={landingData.surveyTopic?.numberColor}
            answerColor={landingData.surveyAnswer?.answerColor}
            />
          </div>

          <div className="surveySection">
            <SurveyQuestion
              topic={landingData.surveyTitle2 || '두 번째 설문'}
              answers={[
                landingData.surveyOpt2_1,
                landingData.surveyOpt2_2,
                landingData.surveyOpt2_3,
                landingData.surveyOpt2_4,
                landingData.surveyOpt2_5
              ].filter(Boolean)}
              onChange={setSurvey2Answer}
              questionNumber={2}
              displayType={landingData.surveyTopic?.topic?.[1]?.type || 'radio'}
            numberColor={landingData.surveyTopic?.numberColor}
            answerColor={landingData.surveyAnswer?.answerColor}
            />
          </div>

          {landingData.eventTarget && (
            <div className="eventTarget">
              <p>이벤트 대상자 : <span className="event-target-text">{landingData.eventTarget}</span></p>
            </div>
          )}

          {landingData.benefitDate && (
            <div className="benefitDate">
              <p>혜택 기간 : <BenefitPeriod periodType={landingData.benefitDate} /></p>
            </div>
          )}

          <div className="formStyleClassName">
            <ZentorgaForm
              onSubmit={handleFormSubmit}
              isSubmitting={isSubmitting}
              formStyle={landingData.formStyleClassName || 'gray'}
              consentStyle={landingData.privacyPolicyClassName || 'dark'}
              privacyPolicyName={landingData.privacyPolicyName || '본원'}
              privacyPolicy={landingData.privacyPolicy}
              marketingPolicy={landingData.marketingPolicy}
              buttonComponent={
                <button
                  type="submit"
                  form="zentorga-form"
                  id="postBtn"
                  className={`postBtn ${landingData.postBtnLocate === 'fixed' ? 'postBtn-fixed' : ''}`}
                  disabled={isSubmitting}
                >
                  {landingData.postBtnUrl ? (
                    <img src={landingData.postBtnUrl} alt="신청하기" />
                  ) : (
                    <span>{isSubmitting ? '신청 중...' : '신청하기'}</span>
                  )}
                </button>
              }
            />
          </div>

          {landingData.serviceInfoClassName && (
            <p
              className={`serviceInfoClassName ${landingData.serviceInfoClassName}`}
              id="serviceInfoClassName"
            >
              * 본 서비스는 전국 20여 개 이상의 제휴 병원에서 제공되며,<br />
              상담 신청 정보를 기반으로 전문 상담원이 적합한 제휴 병원을 안내해드립니다.
            </p>
          )}

          {landingData.footerClassName !== null && (landingData.footerClassName !== undefined || landingData.footerDetail) && (
            <footer className={`footerClassName ${landingData.footerClassName || ''}`}>
              {landingData.footerDetail && (
                <p className="footerDetail" style={{ whiteSpace: 'pre-line' }}>
                  {landingData.footerDetail}
                </p>
              )}
            </footer>
          )}
        </div>
      )}
    </section>
  );
};

export default Template25;
